import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import { ComponentTheme } from '@helpers/themeEnum';
import { h3 } from '@helpers/typography';
import { GetInTouchButton } from '@stories/Components/Buttons/GeneralButton/Button';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import { ArticleCardStyles } from '@stories/Components/Cards/ArticleCard/ArticleCard.styles';
import Controls from '@stories/Components/Carousels/Carousel/Controls/Controls';
import MotionTrack from '@stories/Components/Carousels/Carousel/MotionTrack/MotionTrack';
import SectionWrapper from '@stories/Components/Global/SectionWrapper/SectionWrapper';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  overflow-x: hidden;
  --articlesBgColor: ${brand.white};
  --cardPlaceholderBgColor: ${brand.blue.base};

  @media ${until(Device.TabletLarge)} {
    --itemGutter: 40px;
    --visibleItems: 3 !important;
  }

  @media ${until(Device.Tablet)} {
    --itemGutter: 20px;
    --visibleItems: 2 !important;
  }

  @media ${until(Device.TabletSmall)} {
    --itemGutter: 8px;
    --visibleItems: 1 !important;
  }

  &[data-theme=${ComponentTheme.Black}] {
    --articlesBgColor: ${brand.black};
    --textColour: ${brand.white};
    --cardPlaceholderBgColor: ${brand.grey.grey40};
    color: var(--textColour);

    ${ArticleCardStyles.ContentInner} {
      color: var(--textColour);
    }

    ${ArticleCardStyles.FakeButton} {
      ${ButtonStyles.Button} {
        --textColour: ${brand.white};
      }
    }
  }

  &[data-theme=${ComponentTheme.Blue}] {
    --articlesBgColor: ${brand.blue.tint};
  }

  &[data-theme=${ComponentTheme.Green}] {
    --articlesBgColor: ${brand.green.tint};
    --cardPlaceholderBgColor: ${brand.green.base};
  }

  &[data-theme=${ComponentTheme.Orange}] {
    --articlesBgColor: ${brand.orange.tint};
    --cardPlaceholderBgColor: ${brand.orange.base};
  }

  &[data-theme=${ComponentTheme.Yellow}] {
    --articlesBgColor: ${brand.yellow.tint};
    --cardPlaceholderBgColor: ${brand.yellow.base};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--articlesBgColor);
    z-index: -2;
  }

  ${ArticleCardStyles.MediaHolder} {
    max-height: 420px;

    [data-count='3'] & {
      @media ${from(Device.TabletSmall)} {
        --imageRatio: 428 / 420;
      }
    }
  }
`;
const SectionIntro = styled.div `
  @media ${from(Device.Tablet)} {
    display: flex;
    justify-content: space-between;
  }
`;
const Title = styled.h2 `
  ${h3};

  @media ${from(Device.Tablet)} {
    margin-bottom: 0;
    padding-right: ${fluid(40, 80)};
  }
`;
const ViewAllCta = styled(GetInTouchButton) `
  margin-left: auto;

  @media ${until(Device.Tablet)} {
    display: none;
  }

  ${Container}[data-theme=${ComponentTheme.Black}] & {
    --textColour: ${brand.white};
    --brandColour: ${brand.white};
  }
`;
const MobileViewAllCta = styled(ViewAllCta) `
  display: none;
  margin-top: 30px;

  @media ${until(Device.Tablet)} {
    display: inline-block;
  }
`;
const Track = styled(MotionTrack) `
  margin-top: ${fluid(40, 56)};

  ul {
    gap: 0;

    @media ${from(Device.TabletLarge)} {
      gap: 40px;
    }
  }
`;
const Item = styled.li `
  display: block;
  flex: 0 0 calc(100% / var(--visibleItems));
  padding-right: var(--itemGutter);

  @media ${from(Device.TabletLarge)} {
    flex: 0 1 calc(100% / var(--visibleItems));
  }
`;
const PaginationWrapper = styled.div `
  margin: 2rem 0 0;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 0.5rem;
  align-items: center;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const Pagination = styled.div `
  grid-column: span 9;

  @media ${from(Device.Tablet)} {
    grid-column: 3 / span 8;
  }
`;
const StyledControls = styled(Controls) `
  grid-column: 10 / span 3;
  justify-self: end;
  display: flex;
  gap: 0.5rem;

  @media ${from(Device.Tablet)} {
    grid-column: 11 / span 2;
  }
`;
const Button = styled.button `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background-color: ${brand.black};
  border: none;
  border-radius: 5000000px;
  color: ${brand.white};
  transition: background-color 0.3s;

  &:disabled {
    opacity: 0.5;

    &:hover {
      background-color: ${brand.black};
      opacity: 0.5;
    }
  }

  &:hover {
    background-color: ${brand.grey.grey40};
  }

  svg {
    height: 15px;
  }
`;
export const LatestArticlesStyles = {
    Button,
    Container,
    Controls: StyledControls,
    Item,
    MobileViewAllCta,
    Pagination,
    PaginationWrapper,
    SectionIntro,
    StyledControls,
    Title,
    Track,
    ViewAllCta,
};
